import { createContext, useContext, type MutableRefObject } from "react"
import type { ReactNode } from "react"
import type { Table, Row, VisibilityState } from "@tanstack/react-table"
import type { FilterGroup, PageInfo } from "./table.types"

export type TablePanel = "filter" | "advancedFilter" | "columns" | null

export type RowToolbarConfig = {
  column: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render(row: any): ReactNode
}

export type RowActionsConfig = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render(row: any): ReactNode
}

type TableContextValue = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callbacksRef: MutableRefObject<{ onRowClick?: (row: Row<any>) => void }>
  /** Id of the row whose inline detail panel is expanded, or null. */
  expandedRowId: string | null
  /** Renders the inline detail panel for the expanded row. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderRowDetail: ((row: Row<any>) => ReactNode) | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getHighlightedRowId: ((row: Row<any>) => boolean) | null
  /** The `v` param of the view sheet open right now, or null. */
  activeView: string | null
  rowToolbarRef: MutableRefObject<RowToolbarConfig | null>
  rowActionsRef: MutableRefObject<RowActionsConfig | null>
  lastSelectedRowIdRef: MutableRefObject<string | null>
  activePanel: TablePanel
  setActivePanel(panel: TablePanel): void
  isCellsWrapped: boolean
  hasPersistence: boolean
  defaultColumnVisibility: VisibilityState
  resetTableState(): void
  /**
   * Clears all query state — column filters, the search box, the advanced
   * filter tree, sorting, and pagination — back to defaults, in one call.
   * Generic across every table; surfaced as the toolbar "reset" button.
   */
  resetQueryState(): void
  updateUserColumnVisibility(update: VisibilityState): void
  clearUserColumnVisibility(): void
  /**
   * Advanced (nested AND/OR/NOT) filter tree state. `null` means no
   * advanced filter is active. Set via the advanced-filter builder
   * panel; emitted to the server through `onQueryChange`.
   */
  advancedFilter: FilterGroup | null
  setAdvancedFilter(tree: FilterGroup | null): void
  /**
   * When `enableInfiniteScroll` is on, the table itself synthesises
   * a `pageInfo` (next-page check over the filtered row count) and a
   * fetch-more callback that grows the rendered slice. `Table.Content`
   * uses these as fallbacks if the consumer didn't pass its own.
   */
  infiniteScrollPageInfo: PageInfo | null
  infiniteScrollFetchMore: (() => void) | null
  /**
   * The "Showing X of Y" facts, synced up from `Table.Content` (which
   * owns pageInfo and the count label) so the toolbar can render the
   * count inline instead of spending a whole line on it.
   */
  countMeta: { total: number | null; label: string }
  setCountMeta(meta: { total: number | null; label: string }): void
  /**
   * `Table.Search` registers the filter it writes (a column id, or
   * `GLOBAL_SEARCH_TARGET`), so the table can tell a search apart from any
   * other filter; `null` when it unmounts.
   */
  registerSearch(target: string | null): void
  /**
   * True while the search box has text and the status pills are paused:
   * their filters are left out of what the table applies, so a search
   * covers every row. The pills keep their selection and resume when the
   * search is cleared — or when the operator picks a pill during the
   * search (`resumeStatusFilter`), which narrows the search by it.
   */
  isStatusFilterPaused: boolean
  resumeStatusFilter(): void
}

const TableContext = createContext<TableContextValue | null>(null)

export function useTableContext<TData = unknown>() {
  const context = useContext(TableContext)
  if (!context) {
    throw new Error("useTableContext must be used within a Table")
  }
  return context as {
    table: Table<TData>
    callbacksRef: MutableRefObject<{ onRowClick?: (row: Row<TData>) => void }>
    expandedRowId: string | null
    renderRowDetail: ((row: Row<TData>) => ReactNode) | null
    getHighlightedRowId: ((row: Row<TData>) => boolean) | null
    activeView: string | null
    rowToolbarRef: MutableRefObject<RowToolbarConfig | null>
    rowActionsRef: MutableRefObject<RowActionsConfig | null>
    lastSelectedRowIdRef: MutableRefObject<string | null>
    activePanel: TablePanel
    setActivePanel(panel: TablePanel): void
    isCellsWrapped: boolean
    hasPersistence: boolean
    defaultColumnVisibility: VisibilityState
    resetTableState(): void
    resetQueryState(): void
    updateUserColumnVisibility(update: VisibilityState): void
    clearUserColumnVisibility(): void
    advancedFilter: FilterGroup | null
    setAdvancedFilter(tree: FilterGroup | null): void
    infiniteScrollPageInfo: PageInfo | null
    infiniteScrollFetchMore: (() => void) | null
    countMeta: { total: number | null; label: string }
    setCountMeta(meta: { total: number | null; label: string }): void
    registerSearch(target: string | null): void
    isStatusFilterPaused: boolean
    resumeStatusFilter(): void
  }
}

export const TableProvider = TableContext.Provider
